/**
 * One revision's programme learning outcomes, grouped against the other's.
 *
 * @module components/portfolio/comparison/ProgrammeOutcomeInventory
 */

import React from "react";

import { InventoryPanel } from "@/components/portfolio/comparison/InventoryPanel";
import { Badge, MutedText } from "@/components/ui";
import type { PLO, Programme } from "@/types";
import { stableJson } from "@/utils/stable-json";

export interface ProgrammeOutcomeInventoryProps {
  /** The revision this side belongs to. */
  label: string;
  programme: Programme;
  /** The revision being compared against, which decides what counts as changed. */
  otherProgramme: Programme;
  /** Which revision this is: an outcome missing from the other side was removed or added. */
  side: "before" | "after";
}

/**
 * @example
 * ```tsx
 * <ProgrammeOutcomeInventory label={to.institutionalLabel} programme={to.data} otherProgramme={from.data} side="after" />
 * ```
 */
export function ProgrammeOutcomeInventory({
  label,
  programme,
  otherProgramme,
  side,
}: ProgrammeOutcomeInventoryProps): React.ReactElement {
  const otherById = new Map((otherProgramme.plos ?? []).map((outcome) => [outcome.id, outcome]));
  const outcomes = programme.plos ?? [];
  const groups = [
    {
      label: "Changed outcomes",
      tone: "warning" as const,
      items: outcomes.filter((outcome) => {
        const other = otherById.get(outcome.id);
        return other !== undefined && stableJson(other) !== stableJson(outcome);
      }),
    },
    {
      label: side === "before" ? "Removed in later version" : "New additions",
      tone: side === "before" ? ("error" as const) : ("success" as const),
      items: outcomes.filter((outcome) => !otherById.has(outcome.id)),
    },
    {
      label: "Unchanged outcomes",
      tone: "ghost" as const,
      items: outcomes.filter((outcome) => {
        const other = otherById.get(outcome.id);
        return other !== undefined && stableJson(other) === stableJson(outcome);
      }),
    },
  ];

  return (
    <InventoryPanel<PLO, (typeof groups)[number]>
      label={label}
      name="programme outcomes"
      groupNamePrefix="programme outcome "
      noun={{ one: "outcome", many: "outcomes" }}
      groups={groups}
      itemKey={(outcome) => outcome.id}
      emptyMessage="No programme learning outcomes recorded."
      renderItem={(outcome, group) => (
        <>
          <div className="min-w-0">
            <div className="font-medium">{outcome.text}</div>
            <MutedText size="caption" className="mt-1">
              {outcome.code ?? "No outcome code"}
            </MutedText>
          </div>
          <Badge size="sm" tone={group.tone}>
            {group.label.replace(" outcomes", "").replace(" in later version", "")}
          </Badge>
        </>
      )}
    />
  );
}
